import type { Article } from '../types';
import { h2, h3, p, ul, ol, callout, code, table } from '../b';

const article: Article = {
  slug: 'telegram-mini-app-stars-payments',
  title: 'Accept Telegram Stars Inside a Mini App',
  metaTitle: 'Telegram Mini App Stars Payments with openInvoice',
  metaDescription:
    'Charge Telegram Stars from a Mini App: createInvoiceLink with XTR, WebApp.openInvoice, the status callback, and when not to build it.',
  keywords: [
    'telegram mini app payments',
    'telegram stars mini app',
    'webapp openinvoice',
    'telegram mini app stars',
    'createinvoicelink mini app',
    'telegram webapp payment',
  ],
  category: 'Developers',
  datePublished: '2026-10-21',
  dateModified: '2026-10-21',
  readingTimeMin: 8,
  excerpt:
    'A Mini App cannot charge a card on iOS. It can open a Stars invoice. You create the link on your server with currency XTR, call WebApp.openInvoice, and fulfill the order only after your bot receives successful_payment.',
  keyTakeaways: [
    'Never create the invoice in the Mini App. Call your server, which calls createInvoiceLink with currency XTR and an empty provider token.',
    'Open the sheet with Telegram.WebApp.openInvoice(url, callback). The callback status is not proof of payment.',
    'Fulfill the order from the bot update successful_payment, and store telegram_payment_charge_id.',
    'If you only need a link, a file or a channel membership, Telegram Stars Payment Platform already does this and sends you a webhook.',
  ],
  body: [
    h2('The flow that actually gets you paid'),
    ol([
      'The Mini App asks your backend for an invoice for a known product ID. Do not trust a price sent by the client.',
      'The backend calls `createInvoiceLink` with `currency: "XTR"`.',
      'The Mini App passes that URL to `Telegram.WebApp.openInvoice`.',
      'Telegram shows the native sheet. The user pays or cancels.',
      'Your bot receives `pre_checkout_query` (answer within 10 seconds) and then `successful_payment`.',
      'Only then do you unlock the item, credit the balance, or mark the order paid.',
    ]),
    callout(
      'openInvoice’s callback can say "paid" before your server has stored the charge, or the user can kill the Mini App after paying. The source of truth is the bot update, not the callback.',
    ),

    h2('Create the link on the server'),
    p('Stars invoices use one price line. The amount is the number of Stars, not cents. `provider_token` stays empty.'),
    code(
      `const link = await bot.api.createInvoiceLink(
  "Extra lives",
  "5 extra lives in this Mini App",
  orderId,          // payload, 1–128 bytes, your order id
  "",               // provider_token empty for XTR
  "XTR",
  [{ label: "Extra lives", amount: 50 }],
);`,
      'typescript',
    ),
    p('Full parameter notes and the pre-checkout handler are in the [Bot API guide](/blog/telegram-stars-bot-api-developer-guide).'),

    h2('Open it from the Mini App'),
    code(
      `const invoiceUrl = await fetch("/api/invoice", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ sku: "lives-5" }),
}).then((r) => r.text());

Telegram.WebApp.openInvoice(invoiceUrl, (status) => {
  if (status === "paid") {
    // Optimistic UI only. Confirm with your server.
    pollOrderUntilPaid();
  } else if (status === "cancelled") {
    showIdle();
  } else {
    showFailed();
  }
});`,
      'javascript',
    ),
    p(
      'Load `telegram-web-app.js` before this runs, and call `Telegram.WebApp.ready()`. The invoice URL is a Telegram link. Do not wrap it in your own domain if you want the native sheet; a redirect is fine only if it ends on that URL without an extra interstitial.',
    ),

    h2('Statuses you will see'),
    table(
      ['openInvoice status', 'What it means', 'What to do'],
      [
        ['paid', 'The sheet completed', 'Poll your server. Unlock when successful_payment is stored.'],
        ['cancelled', 'The user closed the sheet', 'Leave the order unpaid.'],
        ['failed', 'The charge did not go through', 'Let them retry. Do not unlock.'],
        ['pending', 'Not finished yet', 'Keep waiting. Rare for Stars.'],
      ],
    ),

    h3('Answer pre_checkout_query'),
    p(
      'If nobody answers within 10 seconds, Telegram fails the payment and the Mini App sees `failed`. The handler should check that the order exists and the amount matches, then call `answerPreCheckoutQuery`. Do not call a slow third party from that handler.',
    ),

    h2('When not to build this'),
    p(
      'Build a Mini App checkout when the purchase is an item inside a game or a tool the user already has open. Do not build one when the product is a link in a channel bio, an ebook, or a private channel. Those are payment links:',
    ),
    ul([
      'A reusable or single-use link: [payment links](/blog/telegram-stars-payment-links).',
      'A file sent after payment: [automatic delivery](/blog/deliver-files-with-telegram-stars).',
      'A 30-day channel: [subscriptions](/blog/telegram-stars-subscriptions).',
    ]),
    p(
      'Telegram Stars Payment Platform exposes a REST API and a webhook for the same ledger, 21-day maturity and TON withdrawal, with a 3% fee. Use that when you want the money movement without operating `pre_checkout_query` yourself.',
    ),
  ],
  faq: [
    {
      q: 'Can a Mini App use Stripe on iOS?',
      a: 'Digital goods inside Telegram on iOS and Android are expected to use Stars. A card form for a digital item is what Stars were introduced to replace.',
    },
    {
      q: 'Does openInvoice work on desktop Telegram?',
      a: 'Yes. The same invoice URL opens the native payment sheet on iOS, Android, desktop and Telegram Web.',
    },
    {
      q: 'Where is the payer’s card number?',
      a: 'Nowhere on your server. Stars payments do not hand you card data. You receive the Stars amount, your payload, and telegram_payment_charge_id.',
    },
    {
      q: 'Can the Mini App create the invoice with the bot token?',
      a: 'Do not ship the bot token in the Mini App. Anything in the client can be read. Create the link on the server.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-bot-api-developer-guide',
    'telegram-stars-payment-links',
    'how-to-accept-telegram-stars-payments',
    'deliver-files-with-telegram-stars',
  ],
};

export default article;
